import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { HelpCircle, MessageSquare, BookOpen, ChevronRight, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

const FAQItem = ({ question, answer, source, url }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="border border-slate-200 rounded-xl bg-white overflow-hidden transition-all duration-200 hover:border-blue-300">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-slate-50 transition-colors"
                aria-expanded={isOpen}
            >
                <h3 className="text-base font-bold text-slate-800 pr-8">{question}</h3>
                <div className={`text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-500' : ''}`}>
                    <ChevronDown size={20} />
                </div>
            </button>
            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
            >
                <div className="p-5 pt-0 text-slate-600 text-sm leading-relaxed border-t border-slate-100/50">
                    <p className="mb-3">{answer}</p>
                    {source && url && (
                        <div className="flex items-center gap-2 text-xs text-slate-400 mt-3 pt-3 border-t border-slate-50">
                            <span className="font-semibold">Source :</span>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-blue-500 hover:text-blue-700 hover:underline transition-colors"
                            >
                                {source} <ExternalLink size={10} />
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const FAQPage = () => {
    const articles = [
        {
            title: "Qu'est-ce que le programme VIE ?",
            excerpt: "Le Volontariat International en Entreprise est une mission professionnelle à l'étranger...",
            category: "Programme"
        },
        {
            title: "Quelles sont les conditions d'éligibilité ?",
            excerpt: "Pour partir en VIE, vous devez avoir entre 18 et 28 ans et être ressortissant...",
            category: "Critères"
        },
        {
            title: "Quelle est la rémunération en VIE ?",
            excerpt: "L'indemnité mensuelle varie selon le pays de destination et est fixée par Business France...",
            category: "Infos pratiques"
        }
    ];

    const faqCategories = [
        {
            category: "1. Généralités & Éligibilité",
            items: [
                {
                    question: "Qu'est-ce que le V.I.E exactement?",
                    answer: "Une mission de service civique. Ce n'est pas un contrat de travail classique, mais une mission encadrée par l'État français dans une entreprise française à l'étranger, placée sous la tutelle de l'Ambassade.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "Quelles sont les limites d'âge pour partir?",
                    answer: "18 à 28 ans. Vous devez avoir 18 ans révolus et démarrer la mission avant votre 29ème anniversaire. Aucune exception n'est accordée.",
                    source: "Business France",
                    url: "https://mon-vie-via.businessfrance.fr/informations-pratiques-sur-le-volontariat-international"
                },
                {
                    question: "Le V.I.E est-il réservé aux Français?",
                    answer: "Non. Il est ouvert à tous les ressortissants de l'Espace Économique Européen (EEE). Un candidat belge, allemand ou italien est 100% éligible.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "Un casier judiciaire vierge est-il requis?",
                    answer: "Oui. L'agrément V.I.E exige un casier judiciaire vierge, car le volontaire représente la France et doit obtenir des visas de travail souvent stricts.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "Peut-on faire un V.I.E pendant ses études?",
                    answer: "Non. C'est une mission à temps plein. Toutefois, elle peut valider un stage de fin d'études si une convention est signée avec votre école.",
                    source: "Business France",
                    url: "https://mon-vie-via.businessfrance.fr/faq-vie-via"
                },
                {
                    question: "Quelle est la durée d'une mission V.I.E?",
                    answer: "De 6 à 24 mois. La mission est renouvelable une fois, dans la limite absolue de 24 mois cumulés.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                }
            ]
        },
        {
            category: "2. Recherche & Stratégie (Trouve+)",
            items: [
                {
                    question: "Toutes les offres sont-elles sur le site officiel?",
                    answer: "Non. De nombreuses entreprises publient uniquement sur leurs sites carrières. Utiliser un agrégateur qui centralise toutes ces sources (comme TrouvetonVIE) est indispensable pour voir 100% du marché.",
                    source: "TrouvetonVIE",
                    url: "https://www.trouvetonvie.com"
                },
                {
                    question: "Comment ne pas rater une nouvelle offre?",
                    answer: "Via des alertes instantanées. Les meilleures missions partent en quelques heures. Un système de notification temps réel est plus efficace qu'une veille manuelle quotidienne.",
                    source: "TrouvetonVIE",
                    url: "https://www.trouvetonvie.com/premium"
                },
                {
                    question: "La candidature spontanée fonctionne-t-elle?",
                    answer: "Oui, c'est le \"marché caché\". 66% des V.I.E sont recrutés par des PME. Contacter une entreprise sans offre active est très efficace si vous lui présentez les avantages financiers du dispositif.",
                    source: "PVTistes",
                    url: "https://pvtistes.net/dossiers/la-formule-win-win-des-entreprises-et-des-jeunes-diplomes-le-vie/"
                },
                {
                    question: "Comment savoir quelles entreprises contacter spontanément?",
                    answer: "En analysant l'historique. Consulter une base d'offres expirées (feature Trouve+) permet d'identifier les sociétés qui recrutent régulièrement dans votre secteur et de les contacter au bon moment.",
                    source: "TrouvetonVIE",
                    url: "https://www.trouvetonvie.com/premium"
                },
                {
                    question: "Comment s'assurer que mon CV correspond à l'offre?",
                    answer: "Par le CV Matching. Les recruteurs utilisent des ATS (logiciels de tri). Utiliser un outil d'analyse sémantique pour aligner votre CV sur les mots-clés de l'offre augmente vos chances.",
                    source: "APEC",
                    url: "https://corporate.apec.fr/home/espace-medias/2009-2019--les-pratiques-des-rec.html"
                },
                {
                    question: "Que dire dans un mail de candidature spontanée?",
                    answer: "Soyez force de proposition. Utilisez des modèles de mails éprouvés (drafting aid) qui mettent en avant les avantages financiers pour l'entreprise (0 charges, subventions) plutôt que de demander un emploi.",
                    source: "AIESME",
                    url: "https://www.aiesme.org/medias/editor/D%C3%A9crocher%20un%20V.I.E.pdf"
                },
                {
                    question: "Comment convaincre une PME de me recruter?",
                    answer: "Par l'argument financier. Rappelez-leur que le V.I.E est exonéré de charges sociales et éligible aux aides régionales (jusqu'à 50% du coût). Des arguments pré-rédigés sont disponibles sur Trouve+.",
                    source: "Team France Export",
                    url: "https://www.teamfrance-export.fr/solutions/v-i-e"
                }
            ]
        },
        {
            category: "3. Statistiques & Impact Carrière",
            items: [
                {
                    question: "Quelles sont les chances d'être embauché après un V.I.E?",
                    answer: "Excellentes (92%). 92% des anciens volontaires sont en poste moins de 6 mois après leur retour de mission. C'est un tremplin avéré pour l'employabilité.",
                    source: "EDHEC / Business France",
                    url: "https://www.edhec.edu/sites/default/files/2025-11/Enqu%C3%AAte_V.I.E_2025_EdhecXBusinessFrance.pdf"
                },
                {
                    question: "L'entreprise d'accueil propose-t-elle souvent un CDI?",
                    answer: "Oui (68%). Plus de deux tiers des volontaires (68%) reçoivent une proposition d'embauche de leur entreprise d'accueil à la fin de leur mission.",
                    source: "Business France",
                    url: "https://vie.businessfrance.fr/enquete-edhec-les-jeunes-plebiscitent-le-vie/"
                },
                {
                    question: "Le V.I.E permet-il de progresser plus vite?",
                    answer: "Oui. 70% des anciens V.I.E estiment que cette expérience a été un accélérateur de carrière par rapport à un parcours classique en France.",
                    source: "EDHEC / Business France",
                    url: "https://diplomeo.com/actualite-vie_impact_carriere_etude_edhec_business_france"
                },
                {
                    question: "Est-ce une expérience recommandée par les anciens?",
                    answer: "Massivement (97%). Le taux de satisfaction est exceptionnel : 97% des jeunes recommandent le dispositif V.I.E à leurs pairs.",
                    source: "Business France",
                    url: "https://vie.businessfrance.fr/enquete-edhec-les-jeunes-plebiscitent-le-vie/"
                },
                {
                    question: "Améliore-t-on vraiment son niveau de langue?",
                    answer: "Oui. 74% des volontaires déclarent avoir significativement amélioré leur maîtrise d'une langue étrangère durant leur mission.",
                    source: "EDHEC / Business France",
                    url: "https://diplomeo.com/actualite-vie_impact_carriere_etude_edhec_business_france"
                }
            ]
        },
        {
            category: "4. Rémunération & Finances",
            items: [
                {
                    question: "De quoi se compose l'indemnité V.I.E?",
                    answer: "Fixe + Géographique. Une part fixe (772,87€ au 01/01/2025) commune à tous, et une part géographique variable selon le coût de la vie du pays.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "L'indemnité V.I.E est-elle imposable en France?",
                    answer: "Non. Elle est totalement exonérée d'impôt sur le revenu en France et de CSG/CRDS. Le montant affiché est du \"super-net\".",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "Quel est le montant de l'indemnité aux USA en 2025?",
                    answer: "Très élevé. Environ 4 900€ à 5 180€ (New York / Californie) pour compenser le coût de la vie et du logement sur place.",
                    source: "Business France",
                    url: "https://mon-vie-via.businessfrance.fr/indemnite-vie"
                },
                {
                    question: "L'indemnité baisse-t-elle si l'entreprise paie le logement?",
                    answer: "Oui. Un abattement de 20% est appliqué sur la part géographique (et non sur le total) si le logement est fourni par l'entreprise.",
                    source: "Business France",
                    url: "https://mon-vie-via.businessfrance.fr/indemnite-vie"
                },
                {
                    question: "Cotise-t-on pour la retraite française?",
                    answer: "Oui. Le V.I.E valide des trimestres pour la retraite de base du régime général français (1 trimestre validé par 90 jours de mission).",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "Qui paie le billet d'avion?",
                    answer: "L'entreprise. Les frais de voyage aller-retour (début et fin de mission) et le transport des bagages (150kg) sont à la charge de l'entreprise d'accueil.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "Quelles aides existent pour l'entreprise (Argumentaire)?",
                    answer: "Subventions régionales. Des aides comme le \"Pass Export\" (Bretagne, Nouvelle-Aquitaine) couvrent 30 à 50% du coût pour les PME. Un argument décisif pour se faire embaucher.",
                    source: "Afigeo (Synthèse)",
                    url: "https://www.afigeo.asso.fr/wp-content/uploads/2021/01/18/aides-regionales-2020.pdf"
                }
            ]
        },
        {
            category: "5. Démarches & Visas",
            items: [
                {
                    question: "Le visa J1 pour les USA est-il difficile à obtenir?",
                    answer: "C'est une procédure longue. Il faut passer par Business France et respecter des quotas stricts. Le délai moyen est de 2 à 4 mois.",
                    source: "Business France",
                    url: "https://mon-vie-via.businessfrance.fr/destinations/etats-unis"
                },
                {
                    question: "La visite médicale est-elle obligatoire?",
                    answer: "Oui. Une visite médicale et des vaccinations à jour sont obligatoires avant le départ pour valider l'aptitude physique.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "Combien de temps avant le départ faut-il être recruté?",
                    answer: "2 à 3 mois. C'est le délai incompressible pour la validation administrative par Business France et l'obtention des visas.",
                    source: "Business France",
                    url: "https://mon-vie-via.businessfrance.fr/informations-pratiques-sur-le-volontariat-international"
                },
                {
                    question: "Le permis de conduire français est-il valable à l'étranger?",
                    answer: "Cela dépend. En UE oui. Hors UE, un permis international est souvent requis, voire un échange contre un permis local après quelques mois.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "Faut-il ouvrir un compte bancaire local?",
                    answer: "Fortement conseillé. Hors zone Euro, c'est indispensable pour éviter les frais de change au quotidien. En zone SEPA, le compte français suffit souvent.",
                    source: "Business France",
                    url: "https://mon-vie-via.businessfrance.fr/faq-vie-via"
                }
            ]
        },
        {
            category: "6. Vie sur place & Après V.I.E",
            items: [
                {
                    question: "Quelle est la couverture santé du V.I.E?",
                    answer: "Excellente. Vous bénéficiez d'une assurance privée complète (gérée par Business France) qui remplace la Sécu et couvre frais médicaux et rapatriement.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "A-t-on droit à des congés payés?",
                    answer: "Oui. Le statut prévoit 2,5 jours de congés par mois de mission (soit 30 jours par an), à poser en accord avec l'entreprise.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "Peut-on démissionner d'un V.I.E?",
                    answer: "Oui. La rupture anticipée est possible (pour un CDI ou raison personnelle) avec un préavis de 1 à 3 mois, sous réserve de validation par Business France.",
                    source: "Business France",
                    url: "https://mon-vie-via.businessfrance.fr/rupture-contrat-vie"
                },
                {
                    question: "Le V.I.E ouvre-t-il des droits au chômage?",
                    answer: "Oui. Le temps de mission est comptabilisé. Au retour, si vous ne trouvez pas d'emploi immédiatement, vous pouvez vous inscrire à France Travail.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "Peut-on travailler en freelance à côté du V.I.E?",
                    answer: "Non. Le statut exige l'exclusivité totale. Toute activité rémunérée annexe est interdite et peut entraîner la résiliation de la mission.",
                    source: "Service Public",
                    url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F10040"
                },
                {
                    question: "Peut-on partir en couple en V.I.E?",
                    answer: "Difficilement. Le statut est individuel. Le conjoint n'est pas pris en charge (visa, assurance) et doit souvent trouver son propre emploi pour résider sur place.",
                    source: "Business France",
                    url: "https://mon-vie-via.businessfrance.fr/faq-vie-via"
                }
            ]
        },
        {
            category: "7. Bonus : Questions \"People Also Ask\"",
            items: [
                {
                    question: "Quel est le meilleur pays pour un V.I.E en 2025?",
                    answer: "USA et Belgique. Les USA pour le niveau de rémunération, la Belgique pour le volume d'offres. Le Vietnam et le Mexique montent en puissance pour les opportunités à responsabilités.",
                    source: "Business France",
                    url: "https://mon-vie-via.businessfrance.fr/destinations"
                },
                {
                    question: "Peut-on cumuler V.I.E et APL en France?",
                    answer: "Non, généralement. En devenant non-résident fiscal (sauf exceptions), vous perdez l'éligibilité aux aides au logement liées à la résidence principale en France.",
                    source: "CAF",
                    url: "https://www.caf.fr/allocataires/caf-de-la-drome/offre-de-service/thematique-libre/votre-vie-evolue-pensez-signaler-vos-changements"
                },
                {
                    question: "Faut-il parler la langue locale pour un V.I.E?",
                    answer: "Souvent oui. Si l'anglais est le minimum requis (B2/C1), la maîtrise de la langue locale (Allemand, Espagnol) est souvent le critère décisif pour l'embauche.",
                    source: "Business France",
                    url: "https://mon-vie-via.businessfrance.fr/comment-trouver-un-v.i.e"
                }
            ]
        }
    ];

    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqCategories.flatMap(cat =>
            cat.items.map(item => ({
                "@type": "Question",
                "name": item.question,
                "acceptedAnswer": { "@type": "Answer", "text": item.answer }
            }))
        )
    };

    return (
        <div className="min-h-screen bg-[#F0F9FF] text-slate-900 font-inter pt-10 pb-20">
            <Helmet>
                <title>FAQ VIE - 30 questions sur le Volontariat International en Entreprise | Trouve ton VIE</title>
                <meta name="description" content="Réponses aux 30 questions les plus posées sur le VIE : âge, salaire, durée, candidature, fiscalité, retour. Mises à jour 2026." />
                <link rel="canonical" href="https://trouvetonvie.fr/faq" />
                <meta property="og:title" content="FAQ VIE - 30 questions sur le Volontariat International en Entreprise | Trouve ton VIE" />
                <meta property="og:description" content="Réponses aux 30 questions les plus posées sur le VIE : âge, salaire, durée, candidature, fiscalité, retour. Mises à jour 2026." />
                <meta property="og:url" content="https://trouvetonvie.fr/faq" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://trouvetonvie.fr/og-image.png" />
                <meta property="og:locale" content="fr_FR" />
                <meta property="og:site_name" content="Trouve ton VIE" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="FAQ VIE - 30 questions sur le Volontariat International en Entreprise | Trouve ton VIE" />
                <meta name="twitter:description" content="Réponses aux 30 questions les plus posées sur le VIE : âge, salaire, durée, candidature, fiscalité, retour. Mises à jour 2026." />
                <meta name="twitter:image" content="https://trouvetonvie.fr/og-image.png" />
                <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
            </Helmet>
            <div className="max-w-4xl mx-auto px-6">
                <div className="text-center mb-16">
                    <h1 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">Espace Information & FAQ</h1>
                    <p className="text-slate-500 font-medium">Tout ce que vous devez savoir sur le VIE et comment maximiser vos chances.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mb-16">
                    {/* Chatbot Card */}
                    <div className="bg-white rounded-[32px] p-8 shadow-sm border border-slate-100 flex flex-col items-center text-center group cursor-pointer hover:scale-[1.02] transition-all">
                        <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 mb-6 group-hover:scale-110 transition-transform">
                            <MessageSquare size={32} />
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 mb-2">Chatbot intelligent</h2>
                        <p className="text-sm text-slate-500 mb-6">Posez n'importe quelle question sur le VIE, notre IA Mistral vous répond 24h/24.</p>
                        <button className="bg-slate-900 text-white px-8 py-3 rounded-xl font-bold text-sm shadow-xl shadow-slate-900/10 active:scale-95 transition-all">
                            Démarrer la discussion
                        </button>
                    </div>

                    {/* Articles Card */}
                    <div className="bg-blue-600 rounded-[32px] p-8 shadow-xl shadow-blue-600/20 text-white flex flex-col items-center text-center group cursor-pointer hover:scale-[1.02] transition-all">
                        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                            <BookOpen size={32} />
                        </div>
                        <h2 className="text-xl font-bold mb-2">Guides & Articles</h2>
                        <p className="text-sm text-blue-100 mb-6">Explorez nos ressources pour préparer votre départ et réussir votre mission.</p>
                        <button className="bg-white text-blue-600 px-8 py-3 rounded-xl font-bold text-sm shadow-xl active:scale-95 transition-all">
                            Lire les guides
                        </button>
                    </div>
                </div>

                {/* FAQ Dropdown Section */}
                {faqCategories.map((cat, catIdx) => (
                    <div key={catIdx} className="mb-12">
                        <h3 className="text-2xl font-bold text-slate-900 mb-8 px-2 flex items-center gap-2">
                            <span className="bg-blue-100 text-blue-600 text-sm font-black px-2 py-1 rounded-md">{catIdx + 1}</span>
                            {cat.category.replace(/^\d+\.\s/, '')}
                        </h3>
                        <div className="space-y-4">
                            {cat.items.map((item, idx) => (
                                <FAQItem
                                    key={idx}
                                    question={item.question}
                                    answer={item.answer}
                                    source={item.source}
                                    url={item.url}
                                />
                            ))}
                        </div>
                    </div>
                ))}

                <div className="space-y-6 mt-16">
                    <h3 className="text-2xl font-bold text-slate-900 mb-8 px-4">Articles populaires</h3>
                    {articles.map((article, idx) => (
                        <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 flex items-center justify-between group hover:border-blue-200 transition-all cursor-pointer">
                            <div>
                                <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1 block">{article.category}</span>
                                <h4 className="text-lg font-bold text-slate-900 mb-1">{article.title}</h4>
                                <p className="text-sm text-slate-500 line-clamp-1">{article.excerpt}</p>
                            </div>
                            <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-600 transition-all">
                                <ChevronRight size={20} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default FAQPage;
